import { useLayoutEffect } from 'react';
import { useLayoutStore } from '../stores/layoutStore';

/**
 * Stable fingerprint of layout inputs that affect voice dock placement.
 * Avoids subscribing to the full `panels` object, which gets a new reference
 * on every dock update and would re-trigger the effect in a loop.
 */
function selectVoiceDockDeps(state: {
  panels: Record<string, { visible: boolean; x: number; y: number; w: number; h: number; minimized?: boolean }>;
  navSidebarExpanded: boolean;
  navSidebarItemCount: number;
}): string {
  const voice = state.panels.voice;
  if (!voice?.visible) {
    return `hidden:${state.navSidebarExpanded ? '1' : '0'}:${state.navSidebarItemCount}`;
  }

  const parts: string[] = [
    state.navSidebarExpanded ? '1' : '0',
    String(state.navSidebarItemCount),
  ];

  for (const [id, panel] of Object.entries(state.panels)) {
    if (id === 'nav' || !panel.visible) continue;
    parts.push(
      `${id}:${panel.x},${panel.y},${panel.w},${panel.h},${panel.minimized ? 'm' : 'v'}`,
    );
  }

  parts.sort();
  return parts.join('|');
}

/**
 * Recompute voice dock state on viewport resize and panel visibility changes.
 */
export function useVoicePanelPlacement(): void {
  const dockDeps = useLayoutStore(selectVoiceDockDeps);
  const updateVoiceDock = useLayoutStore((s) => s.updateVoiceDock);

  useLayoutEffect(() => {
    updateVoiceDock();
    const onResize = (): void => {
      updateVoiceDock();
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [dockDeps, updateVoiceDock]);
}
